import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { TableBody, TableContainer, TableHead, Typography } from '@mui/material';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../uiComponents/MuiTable/StyledTable.js';
import { DateUtils } from '../../../utils/DateUtils.js';

const cellWrap = { overflowWrap: 'anywhere', wordBreak: 'break-word' };

const VariantCallDataModal = ({ open, onClose, variantName, chromosomalHGVS, dateIdentified, sampleData }) => {
  const samples = Array.isArray(sampleData) ? sampleData : (sampleData ? [sampleData] : []);

  const annotationTable = (label, rows) => (
    <div className="modalSection">
      <div className="sectionHeading center">
        <Typography variant="body1" gutterBottom>{label}</Typography>
      </div>
      <TableContainer component={StyledPaper}>
        {/* Inline styles override StyledTable's 750px minWidth, which overflows the modal. */}
        <StyledTable size="small" aria-label={label + ' table'} style={{ minWidth: 0, tableLayout: 'fixed' }}>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell style={{ width: '20%' }}>Key</StyledTableCell>
              <StyledTableCell style={{ width: '15%' }}>Value</StyledTableCell>
              <StyledTableCell style={{ width: '65%' }}>Description</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? (
              rows.map((annotation, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell style={cellWrap}>{annotation.key}</StyledTableCell>
                  <StyledTableCell style={cellWrap}>{annotation.value !== null && annotation.value !== undefined ? String(annotation.value) : ''}</StyledTableCell>
                  <StyledTableCell style={cellWrap}>{annotation.description}</StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={3}>No data available</StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </div>
  );

  return (
    <Modal
      className="modal"
      overlayClassName="modalOverlay"
      aria-modal="true"
      contentLabel="Variant Call Sample Level Data"
      shouldCloseOnOverlayClick={true}
      isOpen={open}
      onRequestClose={onClose}
    >
      <div className="modalHeader">Variant Call Sample Level Data</div>
      {/* Focusable region so keyboard/Safari users can scroll it and Shift+Tab back from Close. */}
      <div className="modalContent" tabIndex={0} role="region" aria-label="Variant call sample level data">
        <div className="modalSection modalDefinitions">
          <Typography variant="body1" component="span" className="modalDefinitionLabel" gutterBottom><strong>Variant Name:</strong></Typography>
          <Typography variant="body1" component="span" gutterBottom>{variantName}</Typography>
          <Typography variant="body1" component="span" className="modalDefinitionLabel" gutterBottom><strong>Chromosomal HGVS:</strong></Typography>
          <Typography variant="body1" component="span" gutterBottom>{chromosomalHGVS}</Typography>
          <Typography variant="body1" component="span" className="modalDefinitionLabel" gutterBottom><strong>Date Identified:</strong></Typography>
          <Typography variant="body1" component="span" gutterBottom>{DateUtils.FormatLocalDate(dateIdentified)}</Typography>
        </div>
        {samples.length > 0 ? samples.map((sample, index) => (
          <React.Fragment key={sample.patientId ?? index}>
           
            {annotationTable('Site-Level Annotations', sample.fieldValueDetails?.info ?? [])}
            {annotationTable('Sample-Level Annotations', sample.fieldValueDetails?.format ?? [])}
          </React.Fragment>
        )) : (
          <Typography className="noData">No variant call data available</Typography>
        )}
      </div>
      <div className="modalButtons">
        <button
          onClick={onClose}
          className="modalButton"
          aria-label="close variant call sample level data modal">
          Close
        </button>
      </div>
    </Modal>
  );
};

VariantCallDataModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  variantName: PropTypes.string,
  chromosomalHGVS: PropTypes.string,
  dateIdentified: PropTypes.string,
  sampleData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default VariantCallDataModal;
